import { AssignmentStatuses, CONTEXT_LIMITS, GradeStatuses, SubmissionStatuses } from './constants';
import { cleanContextText, pruneEmptyContext, toNumberOrNull } from './contextUtils';

export const getSubmissionStudentName = ({ accountUsers = [], submission }) => {
    const userId = `${submission?.user_id || submission?.api?.user?.id || ''}`;
    const accountUser = accountUsers.find((user) => {
        return [user.id, user.user_id, user.owner_account_id].some((id) => `${id || ''}` === userId);
    });

    return (
        accountUser?.preferredName ||
        accountUser?.display_name ||
        accountUser?.name ||
        submission?.api?.user?.sortable_name ||
        submission?.api?.user?.name ||
        submission?.filename ||
        undefined
    );
};

export const getSubmissionStatus = (submission) => {
    if (!submission) return undefined;
    if (submission.is_failed) return SubmissionStatuses.FAILED;
    if (submission.grading) return SubmissionStatuses.GRADED;
    if (submission.ai) return SubmissionStatuses.NEEDS_REVIEW;
    return submission.workflow_state || submission.status || SubmissionStatuses.IMPORTED;
};

export const buildCourseContext = ({ accountUsers = [], assignments = [], course, submissions = [] }) => {
    if (!course) return undefined;

    const activeStudents = accountUsers.filter(({ active, role }) => active && role === 'student');

    return pruneEmptyContext({
        id: `${course.id || ''}`,
        lms_id: course.api_course_id ? `${course.api_course_id}` : undefined,
        name: course.name || course.title,
        code: course.code || course.course_code,
        term: course.term || course.term_name,
        status: course.active === false ? AssignmentStatuses.INACTIVE : AssignmentStatuses.ACTIVE,
        counts: {
            assignments: assignments.length || undefined,
            students: activeStudents.length || undefined,
            submissions: submissions.length || undefined,
            graded_submissions: submissions.filter((submission) => submission.grading).length || undefined,
            ungraded_submissions:
                submissions.length > 0 ? submissions.filter((submission) => !submission.grading).length : undefined,
            not_pushed_to_lms:
                submissions.length > 0
                    ? submissions.filter((submission) => submission.grading && !submission.pushed_to_lms).length
                    : undefined,
        },
    });
};

const buildAiSettingsContext = (assignment) => {
    if (!assignment) return undefined;

    return pruneEmptyContext({
        grading_enabled: assignment.ai_grading_enabled,
        feedback_enabled: assignment.ai_feedback_enabled,
        student_feedback_enabled: assignment.ai_student_feedback_enabled,
        inline_comments_enabled: assignment.ai_inline_comments_enabled,
        feedback_structure: assignment.ai_feedback_settings?.structure,
        feedback_suggestions: toNumberOrNull(assignment.ai_feedback_settings?.num_suggestions),
        suggestion_length: assignment.ai_feedback_settings?.suggestion_length,
        comment_length: assignment.ai_inline_comments_settings?.comment_length,
        language_level:
            assignment.ai_feedback_settings?.language_level || assignment.ai_inline_comments_settings?.language_level,
        tone: assignment.ai_feedback_settings?.tone || assignment.ai_inline_comments_settings?.tone,
        custom_instructions: cleanContextText(assignment.ai_feedback_settings?.custom_instructions, 500),
    });
};

export const buildAssignmentContext = ({
    assignment,
    includeDescriptionText = false,
    includeSettings = true,
    submissions = [],
}) => {
    if (!assignment) return undefined;

    const descriptionText = cleanContextText(assignment?.description, CONTEXT_LIMITS.MAX_TEXT_CHARS);
    const counts =
        submissions.length > 0
            ? {
                  submissions: submissions.length,
                  imported_submissions: submissions.length,
                  graded_submissions: submissions.filter((submission) => submission.grading).length,
                  ungraded_submissions: submissions.filter((submission) => !submission.grading).length,
                  not_pushed_to_lms: submissions.filter((submission) => submission.grading && !submission.pushed_to_lms)
                      .length,
                  needs_grading: submissions.filter((submission) => !submission.grading && submission.ai).length,
              }
            : undefined;

    return pruneEmptyContext({
        id: assignment.id,
        lms_id: assignment.api_assignment_id ? `${assignment.api_assignment_id}` : undefined,
        course_id: assignment.course_id ? `${assignment.course_id}` : undefined,
        title: assignment.title || assignment.name,
        status:
            assignment.active === false
                ? AssignmentStatuses.INACTIVE
                : assignment.is_draft
                  ? AssignmentStatuses.DRAFT
                  : AssignmentStatuses.ACTIVE,
        published: assignment.api_data?.published,
        grading_type: assignment.api_data?.grading_type,
        points_possible: toNumberOrNull(assignment.api_data?.points_possible),
        due_at: assignment.api_data?.due_at,
        citation_format: assignment.citation_format,
        description_text: includeDescriptionText ? descriptionText : undefined,
        ai_settings: includeSettings ? buildAiSettingsContext(assignment) : undefined,
        counts,
    });
};

export const buildRubricContext = ({ full = false, rubric }) => {
    if (!rubric) return undefined;

    const criteria = rubric.criteria || [];

    return pruneEmptyContext({
        id: rubric.id,
        title: rubric.title,
        total_points: criteria.reduce((total, criterion) => {
            const points = Math.max(...(criterion.options || []).map((option) => toNumberOrNull(option.rate) ?? 0), 0);
            return total + points;
        }, 0),
        sort: rubric.sort,
        criteria_count: criteria.length || undefined,
        criteria: full
            ? criteria.slice(0, CONTEXT_LIMITS.RUBRIC_CRITERIA).map((criterion) =>
                  pruneEmptyContext({
                      id: criterion.id,
                      title: criterion.title,
                      description: cleanContextText(criterion.description, 500),
                      points: Math.max(
                          ...(criterion.options || []).map((option) => toNumberOrNull(option.rate) ?? 0),
                          0
                      ),
                      options: (criterion.options || []).slice(0, CONTEXT_LIMITS.RUBRIC_OPTIONS).map((option) =>
                          pruneEmptyContext({
                              id: option.id,
                              title: option.title,
                              description: cleanContextText(option.description, 500),
                              points: toNumberOrNull(option.rate),
                          })
                      ),
                  })
              )
            : undefined,
    });
};

const getCriterionPoints = ({ criterion, optionId }) => {
    const option = (criterion?.options || []).find(({ id }) => id === optionId);
    return toNumberOrNull(option?.rate);
};

export const getSubmissionTotalPoints = ({ assignment, submission }) => {
    if (!assignment?.rubric?.criteria || !submission?.grading?.criteria) return undefined;
    if (submission.grade_overrides?.total !== undefined) return toNumberOrNull(submission.grade_overrides.total);

    return assignment.rubric.criteria.reduce((total, criterion) => {
        const override = submission.grade_overrides?.[criterion.id];
        if (override !== undefined) return total + (toNumberOrNull(override) ?? 0);
        return total + (getCriterionPoints({ criterion, optionId: submission.grading.criteria[criterion.id] }) ?? 0);
    }, 0);
};

const getAssignmentTotalPoints = (assignment) => {
    const criteria = assignment?.rubric?.criteria || [];
    if (!criteria.length) return undefined;

    return criteria.reduce((total, criterion) => {
        return total + Math.max(...(criterion.options || []).map((option) => toNumberOrNull(option.rate) ?? 0), 0);
    }, 0);
};

const buildGradeSetContext = ({ assignment, source = {}, status, submission }) => {
    const totalPossible = getAssignmentTotalPoints(assignment);
    const totalPoints =
        source.total_points ??
        source.total ??
        (submission ? getSubmissionTotalPoints({ assignment, submission }) : undefined);

    const criteria = assignment?.rubric?.criteria
        ?.map((criterion) => {
            const optionId = source.criteria?.[criterion.id] || source[criterion.id]?.optionId || source[criterion.id];
            const points = getCriterionPoints({ criterion, optionId });
            return pruneEmptyContext({
                criterion_id: criterion.id,
                option_id: optionId,
                title: criterion.title,
                points,
                max_points: Math.max(...(criterion.options || []).map((option) => toNumberOrNull(option.rate) ?? 0), 0),
                explanation: cleanContextText(
                    source.grading_explanations?.[criterion.id] || source.explanations?.[criterion.id],
                    500
                ),
            });
        })
        .filter(({ option_id }) => option_id);

    return pruneEmptyContext({
        total_points: toNumberOrNull(totalPoints),
        total_possible: totalPossible,
        percentage:
            totalPossible && totalPoints !== undefined && totalPoints !== null
                ? Math.round((Number(totalPoints) / totalPossible) * 1000) / 10
                : undefined,
        status,
        criteria,
    });
};

export const buildGradesContext = ({ assignment, submission }) => {
    if (!submission) return undefined;

    return pruneEmptyContext({
        current: submission.grading
            ? buildGradeSetContext({
                  assignment,
                  source: submission.grading,
                  status: GradeStatuses.DRAFT,
                  submission,
              })
            : undefined,
        ai: submission.ai?.grading
            ? buildGradeSetContext({
                  assignment,
                  source: submission.ai.grading,
                  status: GradeStatuses.SUGGESTED,
              })
            : undefined,
        final: submission.pushed_to_lms
            ? buildGradeSetContext({
                  assignment,
                  source: submission.grading,
                  status: GradeStatuses.PUSHED,
                  submission,
              })
            : undefined,
    });
};

const normalizeFeedbackText = (feedback) => {
    if (!feedback) return undefined;
    if (typeof feedback === 'string') return cleanContextText(feedback, CONTEXT_LIMITS.MAX_TEXT_CHARS);
    return cleanContextText(feedback.commentary || feedback.text || feedback.message, CONTEXT_LIMITS.MAX_TEXT_CHARS);
};

export const buildFeedbackContext = (submission) => {
    if (!submission) return undefined;

    return pruneEmptyContext({
        current: normalizeFeedbackText(submission.grading?.feedback),
        ai: normalizeFeedbackText(submission.ai?.feedback),
        inline_comments: (submission.grading?.inline_comments || submission.ai?.inline_comments || [])
            .slice(0, 12)
            .map((comment) =>
                pruneEmptyContext({
                    id: comment.id,
                    text: cleanContextText(comment.text || comment.comment || comment.message, 500),
                    location: comment.location || comment.page ? `${comment.location || `page ${comment.page}`}` : undefined,
                    criterion_id: comment.criterion_id,
                })
            ),
    });
};

export const buildSubmissionContext = ({ accountUsers = [], assignment, includeText = false, submission }) => {
    if (!submission) return undefined;

    return pruneEmptyContext({
        id: submission.id,
        assignment_id: assignment?.id || submission.assignment_id,
        student_id: submission.user_id || submission.api?.user?.id,
        status: getSubmissionStatus(submission),
        submitted_at: submission.api?.submitted_at,
        imported_at: submission.created_on,
        modified_on: submission.modified_on,
        summary: cleanContextText(
            submission.summary || submission.ai?.summary || submission.filename,
            CONTEXT_LIMITS.MAX_TEXT_CHARS
        ),
        text: includeText ? cleanContextText(submission.text || submission.content, CONTEXT_LIMITS.MAX_TEXT_CHARS) : undefined,
        attachments: (submission.files || submission.attachments || []).slice(0, 8).map((file) =>
            pruneEmptyContext({
                id: file.id,
                file_key: file.key || file.file_key,
                filename: file.name || file.filename,
                mime_type: file.mime_type || file.type,
                summary: cleanContextText(file.summary, 300),
            })
        ),
        metadata: {
            student_display_name: getSubmissionStudentName({ accountUsers, submission }),
        },
    });
};

export const buildStudentContext = ({ accountUsers = [], submission }) => {
    if (!submission) return undefined;
    const displayName = getSubmissionStudentName({ accountUsers, submission });

    return pruneEmptyContext({
        id: submission.user_id || submission.api?.user?.id,
        display_name: displayName,
        role: 'student',
    });
};

export const buildGradingInsightsContext = ({ assignment, submissions = [] }) => {
    const gradedSubmissions = submissions.filter((submission) => submission.grading);
    const totalPossible = getAssignmentTotalPoints(assignment);
    const percentages = gradedSubmissions
        .map((submission) => {
            const totalPoints = getSubmissionTotalPoints({ assignment, submission });
            return totalPossible && totalPoints !== undefined ? (totalPoints / totalPossible) * 100 : null;
        })
        .filter((percentage) => Number.isFinite(percentage));

    const average =
        percentages.length > 0
            ? Math.round((percentages.reduce((total, value) => total + value, 0) / percentages.length) * 10) / 10
            : undefined;

    const sortedPercentages = [...percentages].sort((left, right) => left - right);
    const median =
        sortedPercentages.length > 0 ? sortedPercentages[Math.floor(sortedPercentages.length / 2)] : undefined;

    const gradeDistribution = Array.from({ length: 10 }, (_, index) => {
        const start = index * 10;
        const end = start + 9;
        return {
            label: `${start}-${index === 9 ? 100 : end}`,
            count: percentages.filter((percentage) => percentage >= start && percentage <= (index === 9 ? 100 : end))
                .length,
        };
    }).filter(({ count }) => count > 0);

    const criterionStats = (assignment?.rubric?.criteria || []).map((criterion) => {
        const points = gradedSubmissions
            .map((submission) => {
                if (submission.grade_overrides?.[criterion.id] !== undefined) {
                    return toNumberOrNull(submission.grade_overrides[criterion.id]);
                }
                return getCriterionPoints({ criterion, optionId: submission.grading?.criteria?.[criterion.id] });
            })
            .filter((pointValue) => Number.isFinite(pointValue));

        return pruneEmptyContext({
            criterion_id: criterion.id,
            title: criterion.title,
            average_points:
                points.length > 0
                    ? Math.round((points.reduce((total, value) => total + value, 0) / points.length) * 10) / 10
                    : undefined,
            max_points: Math.max(...(criterion.options || []).map((option) => toNumberOrNull(option.rate) ?? 0), 0),
            option_counts: (criterion.options || [])
                .map((option) => ({
                    option_id: option.id,
                    title: option.title,
                    count: gradedSubmissions.filter((submission) => submission.grading?.criteria?.[criterion.id] === option.id)
                        .length,
                }))
                .filter(({ count }) => count > 0),
        });
    });

    return pruneEmptyContext({
        counts: {
            submissions: submissions.length || undefined,
            graded_submissions: gradedSubmissions.length || undefined,
            ungraded_submissions:
                submissions.length > 0 ? submissions.filter((submission) => !submission.grading).length : undefined,
            not_pushed_to_lms:
                submissions.length > 0
                    ? submissions.filter((submission) => submission.grading && !submission.pushed_to_lms).length
                    : undefined,
        },
        grade_stats: {
            average,
            median: median !== undefined ? Math.round(median * 10) / 10 : undefined,
            min: sortedPercentages.length ? Math.round(sortedPercentages[0] * 10) / 10 : undefined,
            max: sortedPercentages.length
                ? Math.round(sortedPercentages[sortedPercentages.length - 1] * 10) / 10
                : undefined,
        },
        grade_distribution: gradeDistribution,
        criterion_stats: criterionStats,
    });
};
